import { ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { EMPTY, Subscription, combineLatest, of, switchMap, take, takeUntil, Subject } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { patientGroupId } from './../../../../core/utils/patient-group';
import { AnalyticsService } from './../../../../core/services/analytics.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { GroupService } from './../../../../core/services/group.service';
import { PatientLabService } from './../../../../core/services/patient-lab.service';
import { PatientService } from './../../../../core/services/patient.service';
import { UserService } from './../../../../core/services/user.service';
import { LabTestSelectorComponent, LabTestChecklistItem } from './../../../../shared/components/lab-test-selector/lab-test-selector.component';
import { LabOrderConfigDialogComponent } from './../../../../shared/components/lab-order-config-dialog/lab-order-config-dialog.component';

@Component({
    selector: 'app-patient-lab-order-detail',
    templateUrl: './patient-lab-order-detail.component.html',
    styleUrls: ['./patient-lab-order-detail.component.scss']
})
export class PatientLabOrderDetailComponent extends BaseComponent implements OnInit, OnDestroy {
    todayDate: moment.Moment = moment();
    @ViewChild('labTestSelector') labTestSelector!: LabTestSelectorComponent;

    form!: FormGroup;
    editingLabOrder: any;
    patient: any = {};
    groupId!: string;
    labOrderTypes: any[] = [
        { id: 'routine', label: 'Routine' },
        { id: 'urgent', label: 'Urgent' },
        { id: 'stat', label: 'STAT' }
    ];
    reviewStatuses: any[] = [
        { id: 'pending', label: 'Pending Review' },
        { id: 'normal', label: 'Normal' },
        { id: 'alert', label: 'Alert' },
        { id: 'critical', label: 'Critical' }
    ];

    selectedLabTests: LabTestChecklistItem[] = [];
    userPreferredLabTests: any[] = [];
    labTestBundles: any[] = [];
    selectedBundles: any[] = [];
    previousSelectedBundles: any[] = []; // Track previous selection to detect deselected bundles
    allOtherTests: LabTestChecklistItem[] = [];
    isEditing: boolean = false;
    showIndividualTests: boolean = true; // Always show search by default
    
    // User and group data for validation
    currentUser: any = null;
    currentGroup: any = null;
    
    // Physician selection for non-doctor users
    groupUsers: any[] = [];
    selectedPhysicianId: string | null = null;
    showPhysicianSelection: boolean = false;
    get isNonDoctorUser(): boolean {
        return this.currentUser?.userType !== 'doctor';
    }
    get hasSelectedPhysician(): boolean {
        return !this.isNonDoctorUser || !!this.selectedPhysicianId;
    }
    
    get selectedPhysician(): any {
        if (!this.isNonDoctorUser || !this.selectedPhysicianId) {
            return this.currentUser;
        }
        return this.groupUsers.find(u => u.userId === this.selectedPhysicianId) || null;
    }

    get canSubmitLabOrder(): boolean {
        // For non-doctor users, check the selected physician's lancetCode
        // For doctor users, check their own lancetCode
        const physician = this.selectedPhysician;
        const lancetCode = physician?.lancetCode;
        const hasLancetCode = lancetCode && typeof lancetCode === 'string' && lancetCode.trim() !== '';
        
        return !!hasLancetCode;
    }

    private routeParamsSub!: Subscription;
    override destroy$ = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: FormBuilder,
        private patientService: PatientService,
        private groupService: GroupService,
        private patientLabService: PatientLabService,
        private userService: UserService,
        private analyticsService: AnalyticsService,
        override feedbackService: FeedbackService,
        override dialog: MatDialog,
        private cdr: ChangeDetectorRef
    ) {
        super(feedbackService, dialog);
    }

    openLabOrderConfigDialog(event: Event): void {
        event.preventDefault();
        const physician = this.selectedPhysician;
        const dialogRef = this.dialog.open(LabOrderConfigDialogComponent, {
            width: '500px',
            data: {
                currentUser: this.currentUser,
                targetUser: physician, // The physician whose lancetCode needs to be set
                currentGroup: this.currentGroup,
                needsLancetCode: !physician?.lancetCode,
                isUpdatingOtherUser: this.isNonDoctorUser && physician?.userId !== this.currentUser?.id
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // Reload user profile if updating current user
                if (result.lancetCode !== undefined && !result.targetUserId) {
                    this.userService.getUserProfile().subscribe(profile => {
                        this.currentUser = profile;
                        this.cdr.detectChanges();
                    });
                }
                // Reload group users to get updated physician lancetCode
                if (result.targetUserId) {
                    // Trigger a fresh fetch from the server
                    this.groupService.getGroupUsers().subscribe(() => {
                        // The observable will automatically update via groupUsers$ subscription
                        // Reload preferred lab tests for the currently selected physician
                        if (this.selectedPhysicianId) {
                            this.loadUserPreferredLabTests(this.selectedPhysicianId);
                        }
                        this.cdr.detectChanges();
                    });
                }
            }
        });
    }

    ngOnInit(): void {
        console.log('[LabOrderDetail] ngOnInit - initializing component');
        console.log('[LabOrderDetail] Initial state:', {
            currentUser: this.currentUser,
            currentGroup: this.currentGroup,
            canSubmitLabOrder: this.canSubmitLabOrder
        });
        this.initializeForm();
        this.loadGroupUsers();
        
        // Subscribe to user profile changes to update lab test bundles immediately
        // This ensures updates from settings are reflected immediately
        this.userService.userProfile$
            .pipe(takeUntil(this.destroy$))
            .subscribe((profile) => {
                this.currentUser = profile;
                // Only update bundles if we're loading for the current user (not a selected physician)
                if (!this.selectedPhysicianId || this.selectedPhysicianId === profile?.id) {
                    if (profile?.preferredLabTests) {
                        this.labTestBundles = profile.preferredLabTests;
                        // Extract all lab tests from bundles for display
                        this.userPreferredLabTests = profile.preferredLabTests.flatMap((bundle: any) => bundle.labTests || []);
                        this.updateSelectedLabTests();
                        this.updateAllOtherTests();
                    }
                }
            });
        
        this.loadUserPreferredLabTests();
        this.loadData();
    }

    override ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.routeParamsSub) {
            this.routeParamsSub.unsubscribe();
        }
    }

    private initializeForm(): void {
        this.form = this.fb.group({
            orderDate: [moment(), Validators.required],
            priority: ['routine', Validators.required],
            labTests: [[], Validators.required],
            notes: [''],
            reviewStatus: ['pending', Validators.required],
            resultSummary: [''],
            labResults: [''],
            selectedBundles: [[]],
            physicianId: ['']
        });
    }

    private loadUserPreferredLabTests(physicianId?: string, callback?: () => void): void {
        const userId = physicianId || this.currentUser?.id;
        if (!userId) return;

        console.log('[LabOrderDetail] loadUserPreferredLabTests - loading for userId:', userId);
        
        // If loading for current user, the subscription in ngOnInit will handle updates
        // Just load initial data if available
        if (!physicianId || physicianId === this.currentUser?.id) {
            const profile = this.currentUser;
            if (profile?.preferredLabTests) {
                this.labTestBundles = profile.preferredLabTests;
                // Extract all lab tests from bundles for display
                this.userPreferredLabTests = profile.preferredLabTests.flatMap((bundle: any) => bundle.labTests || []);
                this.updateSelectedLabTests();
                this.updateAllOtherTests();
                if (callback) callback();
            } else if (callback) {
                callback(); // Call callback even if no preferred tests yet
            }
        } else {
            // Find physician from group users - they should have preferredLabTests if available
            const physician = this.groupUsers.find(gu => gu.userId === userId);
            if (physician?.preferredLabTests && Array.isArray(physician.preferredLabTests) && physician.preferredLabTests.length > 0) {
                this.labTestBundles = physician.preferredLabTests;
                this.userPreferredLabTests = physician.preferredLabTests.flatMap((bundle: any) => bundle.labTests || []);
                this.updateSelectedLabTests();
                this.updateAllOtherTests();
                console.log('[LabOrderDetail] Loaded preferred lab tests from group users for physician:', userId, 'Bundles:', this.labTestBundles.length);
                if (callback) callback();
            } else {
                console.warn('[LabOrderDetail] Physician preferred lab tests not available in group users for userId:', userId, 'Physician:', physician);
                if (callback) callback(); // Still call callback even if bundles not found
            }
        }
    }
    
    private loadGroupUsers(): void {
        this.groupService.groupUsers$
            .pipe(takeUntil(this.destroy$))
            .subscribe((users: any[]) => {
                if (users?.length) {
                    // Filter to only doctors
                    this.groupUsers = users.filter(u => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50');
                    // Log to verify preferredLabTests are included
                    console.log('[LabOrderDetail] Loaded group users:', this.groupUsers.map(u => ({
                        userId: u.userId,
                        name: `${u.title} ${u.name} ${u.surname}`,
                        hasPreferredLabTests: !!u.preferredLabTests,
                        preferredLabTestsCount: Array.isArray(u.preferredLabTests) ? u.preferredLabTests.length : 0
                    })));
                    this.determinePhysicianSelection();
                }
            });
    }
    
    private determinePhysicianSelection(): void {
        if (this.groupUsers.length === 0) {
            this.selectedPhysicianId = null;
            this.showPhysicianSelection = false;
            return;
        }

        if (this.groupUsers.length === 1) {
            // Only one physician - use as default and don't show selection
            this.selectedPhysicianId = this.groupUsers[0].userId;
            this.showPhysicianSelection = false;
            this.form.patchValue({ physicianId: this.selectedPhysicianId });
            if (this.selectedPhysicianId) {
                this.loadUserPreferredLabTests(this.selectedPhysicianId);
            }
            return;
        }

        // Multiple physicians - check if current user is a doctor
        const currentUser = this.userService.user;
        const isCurrentUserPhysician = this.groupUsers.findIndex(gu => gu.userId === currentUser.id) > -1;

        if (isCurrentUserPhysician) {
            // Current user is a physician - default to them
            this.selectedPhysicianId = currentUser.id;
            this.showPhysicianSelection = false;
            this.form.patchValue({ physicianId: this.selectedPhysicianId });
            if (this.selectedPhysicianId) {
                this.loadUserPreferredLabTests(this.selectedPhysicianId);
            }
        } else {
            // Current user is not a physician - show dropdown, default to group owner
            const groupOwner = this.groupUsers.find(user => user.userId === this.groupService.group?.userId) || this.groupUsers[0];
            this.selectedPhysicianId = groupOwner?.userId || null;
            this.showPhysicianSelection = true;
            if (this.selectedPhysicianId) {
                this.form.patchValue({ physicianId: this.selectedPhysicianId });
                // Load preferred lab tests for the pre-selected physician
                this.loadUserPreferredLabTests(this.selectedPhysicianId);
            }
        }
    }
    
    onPhysicianSelectionChanged(physicianId: string): void {
        this.selectedPhysicianId = physicianId;
        this.form.patchValue({ physicianId: physicianId });
        this.loadUserPreferredLabTests(physicianId);
    }

    private loadData(): void {
        console.log('[LabOrderDetail] loadData - subscribing to combineLatest');
        this.routeParamsSub = combineLatest([
            this.route.params,
            this.patientService.currentPatient$,
            this.groupService.selectedGroup$
        ]).pipe(
            takeUntil(this.destroy$),
            switchMap(([params, patient, group]) => {
               /*  console.log('[LabOrderDetail] combineLatest emitted:', {
                    params: params,
                    patient: patient?.id,
                    group: group,
                    groupFullPracticeNumber: group?.fullPracticeNumber,
                    hasFullPracticeNumber: group?.fullPracticeNumber && typeof group.fullPracticeNumber === 'string' && group.fullPracticeNumber.trim() !== ''
                }); */
                this.patient = patient;
                this.groupId = group?.id;
                this.currentGroup = group;

                if (params['id'] && params['id'] !== 'new') {
                    this.isEditing = true;
                    return this.patientLabService.getLabOrderById(params['id']);
                } else {
                    this.isEditing = false;
                    return of(null);
                }
            })
        ).subscribe((labOrder) => {
            if (labOrder?.success && labOrder.data) {
                this.editingLabOrder = labOrder.data;
                // For non-doctor users editing an existing order, we need to set the physician first
                // so that lab test bundles are loaded before populating the form
                if (this.isNonDoctorUser && this.editingLabOrder.userId) {
                    this.selectedPhysicianId = this.editingLabOrder.userId;
                    this.form.patchValue({ physicianId: this.editingLabOrder.userId });
                    // Load bundles for the physician, then populate form after bundles are loaded
                    this.loadUserPreferredLabTests(this.editingLabOrder.userId, () => {
                        this.populateForm();
                    });
                } else {
                    this.populateForm();
                }
            } else if (!this.isEditing) {
                this.setupNewLabOrder();
            }
        });
    }

    private populateForm(): void {
        if (this.editingLabOrder) {
            this.selectedBundles = this.editingLabOrder.selectedBundles || [];
            this.previousSelectedBundles = [...this.selectedBundles]; // Initialize previous selection
            
            this.form.patchValue({
                orderDate: moment(this.editingLabOrder.orderDate),
                priority: this.editingLabOrder.priority,
                labTests: this.editingLabOrder.labTests || [],
                notes: this.editingLabOrder.notes || '',
                reviewStatus: this.editingLabOrder.reviewStatus,
                resultSummary: this.editingLabOrder.resultSummary || '',
                labResults: this.editingLabOrder.labResults || '',
                selectedBundles: this.selectedBundles
            });

            // Set up lab test checklist items
            // Note: If this is a non-doctor user, bundles should already be loaded
            // from loadUserPreferredLabTests callback before populateForm is called
            if (this.editingLabOrder.labTests && Array.isArray(this.editingLabOrder.labTests)) {
                this.showIndividualTests = true;
                this.selectedLabTests = this.editingLabOrder.labTests.map((test: any) => ({
                    test: test,
                    checked: true,
                    category: (test as any).category || 'General',
                    specimenSource: (test as any).meta?.specimenSource || null
                }));
                this.updateAllOtherTests();
            } else {
                // If no existing lab tests, show search by default
                this.updateSelectedLabTests();
            }
        }
    }

    private setupNewLabOrder(): void {
        this.form.patchValue({
            orderDate: moment(),
            priority: 'routine',
            reviewStatus: 'pending'
        });
        this.updateSelectedLabTests();
    }

    private updateSelectedLabTests(): void {
        // Always show search - allow users to search and add tests
        // Initialize with empty array if not already set
        if (!this.selectedLabTests || this.selectedLabTests.length === 0) {
            this.selectedLabTests = [];
        }
        this.showIndividualTests = true; // Always show search

        // Update the form with current selection
        const currentTests = this.selectedLabTests
            .filter(item => item.checked)
            .map(item => item.test);
        this.form.patchValue({
            labTests: currentTests
        });
    }

    private updateAllOtherTests(): void {
        // Helper to normalize test for comparison (same as backend)
        const normalizeTest = (test: any) => test?.orderCode || test?.code || test?.id;
        
        // Get all tests from selected bundles (deduplicated)
        const selectedBundleTests = this.selectedBundles.flatMap((bundle: any) => bundle.labTests || []);
        const selectedTestCodes = new Set(selectedBundleTests.map((t: any) => normalizeTest(t)));
        
        // Get all tests from all bundles (deduplicated)
        const allTests = this.labTestBundles.flatMap((bundle: any) => bundle.labTests || []);
        const uniqueAllTestsMap = new Map<string, any>();
        allTests.forEach((test: any) => {
            const testKey = normalizeTest(test);
            if (testKey && !uniqueAllTestsMap.has(testKey)) {
                uniqueAllTestsMap.set(testKey, test);
            }
        });
        const uniqueAllTests = Array.from(uniqueAllTestsMap.values());

        // Filter out tests that are already in selected bundles (using orderCode for comparison)
        this.allOtherTests = uniqueAllTests
            .filter(test => {
                const testCode = normalizeTest(test);
                return testCode && !selectedTestCodes.has(testCode);
            })
            .map(test => ({
                test: test,
                checked: false,
                category: (test as any).category || 'General'
            }));
    }

    onBundleSelectionChanged(selectedBundles: any[]): void {
        // Helper to normalize test for comparison (same as backend)
        const normalizeTest = (test: any) => test?.orderCode || test?.code || test?.id;

        // Find bundles that were deselected (in previous but not in current)
        const deselectedBundles = this.previousSelectedBundles.filter(
            prevBundle => !selectedBundles.some(currBundle => currBundle.id === prevBundle.id)
        );

        // If bundles were deselected, remove their tests from selectedLabTests
        if (deselectedBundles.length > 0) {
            // Get all test codes from deselected bundles
            const deselectedBundleTests = deselectedBundles.flatMap((bundle: any) => bundle.labTests || []);
            const deselectedTestCodes = new Set(
                deselectedBundleTests.map((test: any) => normalizeTest(test))
            );

            // Remove tests that came from deselected bundles
            // Keep tests that were added via search (not from any bundle)
            this.selectedLabTests = this.selectedLabTests.filter(item => {
                const testCode = normalizeTest(item.test);
                // Keep the test if it's not from a deselected bundle
                return !deselectedTestCodes.has(testCode);
            });
        }

        // Update selected bundles
        this.selectedBundles = selectedBundles;
        this.previousSelectedBundles = [...selectedBundles]; // Store for next comparison

        if (selectedBundles.length > 0) {
            // Get all tests from selected bundles
            const allBundleTests = selectedBundles.flatMap((bundle: any) => bundle.labTests || []);
            
            // Deduplicate tests using orderCode (same normalization as backend)
            const uniqueTestsMap = new Map<string, any>();
            allBundleTests.forEach((test: any) => {
                const testKey = normalizeTest(test);
                if (testKey && !uniqueTestsMap.has(testKey)) {
                    uniqueTestsMap.set(testKey, test);
                }
            });
            
            // Convert map back to array of unique tests
            const bundleTests = Array.from(uniqueTestsMap.values());
            
            // Get existing test codes from currently selected tests
            const existingTestCodes = new Set(
                this.selectedLabTests.map(item => normalizeTest(item.test))
            );
            
            // Add bundle tests that aren't already in selectedLabTests
            const newBundleTests = bundleTests
                .filter(test => !existingTestCodes.has(normalizeTest(test)))
                .map(test => ({
                    test: test,
                    checked: true,
                    category: test.category || 'General',
                    specimenSource: (test as any).meta?.specimenSource || null
                }));
            
            // Merge with existing selected tests
            this.selectedLabTests = [...this.selectedLabTests, ...newBundleTests];
        }

        this.updateAllOtherTests();

        // Update form with all selected tests
        const allSelectedTests = this.selectedLabTests
            .filter(item => item.checked)
            .map(item => item.test);
        this.form.patchValue({
            labTests: allSelectedTests
        });
        
        console.log('Bundle selection changed:', {
            selectedBundlesCount: selectedBundles.length,
            deselectedBundlesCount: deselectedBundles.length,
            totalSelectedTests: this.selectedLabTests.length
        });
    }

    onLabTestsChanged(checkedItems: LabTestChecklistItem[]): void {
        this.selectedLabTests = checkedItems;
        const selectedTests = checkedItems
            .filter(item => item.checked)
            .map(item => item.test);

        this.form.patchValue({
            labTests: selectedTests
        });
    }

    onLabTestsSelectionChanged(checkedTests: any[]): void {
        // Handle individual test selection changes if needed
        this.cdr.detectChanges();
    }

    onOtherTestsChanged(checkedItems: LabTestChecklistItem[]): void {
        this.allOtherTests = checkedItems;
        const selectedTests = checkedItems
            .filter(item => item.checked)
            .map(item => item.test);

        // Get current form lab tests
        const currentLabTests = this.form.get('labTests')?.value || [];
        
        // Helper to normalize test for comparison (same as backend)
        const normalizeTest = (test: any) => test?.orderCode || test?.code || test?.id;
        
        // Create a set of existing test codes
        const existingTestCodes = new Set(currentLabTests.map((t: any) => normalizeTest(t)));
        
        // Only add tests that aren't already in the form
        const newTests = selectedTests.filter((test: any) => {
            const testCode = normalizeTest(test);
            return testCode && !existingTestCodes.has(testCode);
        });
        
        // Combine existing tests with new tests
        const updatedLabTests = [...currentLabTests, ...newTests];

        console.log('Other tests changed - updating form:', {
            currentCount: currentLabTests.length,
            selectedCount: selectedTests.length,
            newTestsCount: newTests.length,
            updatedCount: updatedLabTests.length
        });

        this.form.patchValue({
            labTests: updatedLabTests
        });
    }

    onOtherTestsSelectionChanged(checkedTests: any[]): void {
        console.log('Other tests selection changed:', checkedTests);
    }

    saveLabOrder(): void {
        if (this.form.valid) {
            // For non-doctor users, require physician selection
            if (this.isNonDoctorUser && !this.selectedPhysicianId) {
                this.showToastMessage('Please select a requesting physician');
                return;
            }

            const hasIdOrPassportNumber = this.patient.idNumber && this.patient.idNumber.trim() !== '' || this.patient.passportNumber && this.patient.passportNumber.trim() !== '';
            if (!hasIdOrPassportNumber) {
                this.showToastMessage('This patient does not have an ID or passport number which is required to submit a lab order. Please enter an ID or passport number to submit a lab order.');
                return;
            }

            this.analyticsService.trackButtonClick('Lab Order Save');

            const formData = this.form.value;
            // Get lab tests from form value (which includes tests added via "Other tests" section)
            // If form value is empty, fall back to selectedLabTests
            const formLabTests = formData.labTests || [];
            let selectedTests = formLabTests.length > 0 
                ? formLabTests 
                : this.selectedLabTests.filter(item => item.checked).map(item => item.test);
            
            // Ensure meta information (including specimenSource) is preserved on test objects
            selectedTests = selectedTests.map((test: any) => {
                // Find the corresponding checklist item to get specimenSource
                const checklistItem = this.selectedLabTests.find(item => {
                    const normalizeTest = (t: any) => t?.orderCode || t?.code || t?.id;
                    return normalizeTest(item.test) === normalizeTest(test);
                });
                
                // Ensure meta object exists and includes specimenSource if available
                if (!test.meta) {
                    test.meta = {};
                }
                if (checklistItem?.specimenSource !== undefined) {
                    test.meta.specimenSource = checklistItem.specimenSource;
                } else if ((test as any).meta?.specimenSource) {
                    // Preserve existing meta.specimenSource if already present
                    test.meta.specimenSource = (test as any).meta.specimenSource;
                }
                
                return test;
            });
            
            const labOrderData = {
                ...formData,
                patientId: this.patient.id,
                groupId: patientGroupId(this.patient, this.groupId),
                orderDate: formData.orderDate.toISOString(),
                labTests: selectedTests,
                status: 'pending',
                labProvider: 'lancet',
                // Pass physicianId if selected (for non-doctor users ordering on behalf of a physician)
                physicianId: this.selectedPhysicianId || formData.physicianId || this.currentUser?.id
            };
            
            console.log('Saving lab order with tests:', selectedTests.length, selectedTests);
            console.log('Tests with meta:', selectedTests.map((t: any) => ({ 
                orderCode: t.orderCode, 
                isMicro: t.isMicro, 
                meta: t.meta 
            })));

            if (this.isEditing && this.editingLabOrder?.id) {
                this.updateLabOrder(labOrderData);
            } else {
                this.createLabOrder(labOrderData);
            }
        } else {
            this.showToastMessage('Please fill in all required fields.');
        }
    }

    private createLabOrder(labOrderData: any): void {
        this.patientLabService.createLabOrder(labOrderData).subscribe({
            next: (response) => {
                if (response?.success) {
                    this.showToastMessage('Lab order created successfully.');
                    this.returnToParent();
                } else {
                    this.showToastMessage('Failed to create lab order.');
                }
            },
            error: (error) => {
                console.error('Error creating lab order:', error);
                this.showToastMessage('Failed to create lab order.');
            }
        });
    }

    private updateLabOrder(labOrderData: any): void {
        this.patientLabService.updateLabOrder(this.editingLabOrder.id, labOrderData).subscribe({
            next: (response) => {
                if (response?.success) {
                        
                    this.showToastMessage('Lab order updated successfully.');
                    
                    // Show change summary if there were test changes
                    if (response?.changes?.hasTestChanges) {
                        const changes = response.changes;
                        const changeMsg = [];
                        if (changes.addedTests > 0) {
                            changeMsg.push(`${changes.addedTests} test(s) added`);
                        }
                        if (changes.removedTests > 0) {
                            changeMsg.push(`${changes.removedTests} test(s) removed`);
                        }
                        if (changeMsg.length > 0) {
                            this.showToastMessage(`Changes: ${changeMsg.join(', ')}`);
                        }
                    }
                    
                    this.returnToParent();
                } else {
                    this.showToastMessage(response?.message || 'Failed to update lab order.');
                }
            },
            error: async (error) => {
                console.error('Error updating lab order:', error);
                
                // Check if this is a Lancet-specific error (consumed/cancelled)
                const errorResponse = error?.error;
                const errorType = errorResponse?.errorType;
                
                if (errorType === 'CONSUMED' || errorType === 'CANCELLED') {
                    // Show confirmation dialog with options
                    const errorMessage = errorResponse?.message || errorResponse?.error || 'The order cannot be updated.';
                    const suggestion = errorResponse?.suggestion || 
                        (errorType === 'CONSUMED' 
                            ? 'You can create a new order with the additional tests instead.'
                            : 'You can create a new order with the tests instead.');
                    
                    const changes = errorResponse?.changes || {};
                    let changeSummary = '';
                    if (changes.addedTests > 0 || changes.removedTests > 0) {
                        const changeMsg = [];
                        if (changes.addedTests > 0) {
                            changeMsg.push(`${changes.addedTests} test(s) added`);
                        }
                        if (changes.removedTests > 0) {
                            changeMsg.push(`${changes.removedTests} test(s) removed`);
                        }
                        changeSummary = `\n\nChanges attempted: ${changeMsg.join(', ')}`;
                    }
                    
                    const fullMessage = `${errorMessage}${changeSummary}\n\n${suggestion}`;
                    
                    // Show confirmation dialog with option to create new order
                    const createNewOrder = await this.showConfirmationDialog(
                        errorType === 'CONSUMED' ? 'Order Already Being Processed' : 'Order Has Been Cancelled',
                        fullMessage,
                        [],
                        'Cancel',
                        'Create New Order',
                        false
                    );
                    
                    if (createNewOrder) {
                        // Create a new order with only the newly added tests
                        this.createNewOrderFromUpdate(labOrderData, errorResponse);
                    }
                } else {
                    // Generic error - show simple message
                    const errorMessage = errorResponse?.message || errorResponse?.error || 'Failed to update lab order.';
                    this.showToastMessage(errorMessage);
                }
            }
        });
    }

    private createNewOrderFromUpdate(labOrderData: any, errorResponse?: any): void {
        // Helper function to normalize test codes for comparison (same as backend)
        const normalizeTest = (test: any) => {
            return test?.orderCode || test?.code || test?.id || JSON.stringify(test);
        };

        // Get original tests from the editing lab order
        const originalTests = Array.isArray(this.editingLabOrder?.labTests) 
            ? this.editingLabOrder.labTests 
            : [];
        
        // Get new tests from the lab order data
        const newTests = Array.isArray(labOrderData?.labTests) 
            ? labOrderData.labTests 
            : [];

        // Create a set of original test codes for quick lookup
        const originalTestCodes = new Set(originalTests.map((test: any) => normalizeTest(test)));

        // Find only the newly added tests (tests in newTests but not in originalTests)
        const addedTests = newTests.filter((test: any) => {
            const testCode = normalizeTest(test);
            return testCode && !originalTestCodes.has(testCode);
        });

        // If no tests were added, show a message and return
        if (addedTests.length === 0) {
            this.showToastMessage('No new tests were added to create a new order.');
            return;
        }

        // Create a new order with only the newly added tests
        const newOrderData = {
            ...labOrderData,
            // Only include the newly added tests
            labTests: addedTests,
            // Remove the order ID to create a new order
            id: undefined,
            orderNumber: undefined,
            status: 'pending'
        };

        this.patientLabService.createLabOrder(newOrderData).subscribe({
            next: (response) => {
                if (response?.success) {
                    this.showToastMessage(`New lab order created successfully with ${addedTests.length} newly added test(s).`);
                    this.returnToParent();
                } else {
                    this.showToastMessage('Failed to create new lab order.');
                }
            },
            error: (error) => {
                console.error('Error creating new lab order:', error);
                this.showToastMessage('Failed to create new lab order.');
            }
        });
    }

    deleteLabOrder(): void {
        if (this.editingLabOrder?.id) {
            this.analyticsService.trackButtonClick('Lab Order Delete');

            this.patientLabService.deleteLabOrder(this.editingLabOrder.id).subscribe({
                next: (response) => {
                    if (response?.success) {
                        this.showToastMessage('Lab order deleted successfully.');
                        this.returnToParent();
                    } else {
                        this.showToastMessage('Failed to delete lab order.');
                    }
                },
                error: (error) => {
                    console.error('Error deleting lab order:', error);
                    this.showToastMessage('Failed to delete lab order.');
                }
            });
        }
    }

    returnToParent() {
        this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patient.id}`] } }]);
    }

    get isProcedureNote(): boolean {
        return this.form.get('isProcedure')?.value || false;
    }

    get canEdit(): boolean {
        return this.isEditing && this.editingLabOrder?.status !== 'completed';
    }

    get canDelete(): boolean {
        return this.isEditing && this.editingLabOrder?.status === 'pending';
    }
}
